import PropTypes from 'prop-types'
import { memo, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import constants from 'app/constants'
import { useUserContext } from 'app/services/UserProvider/UserProvider'
import { useScrollRegionContext } from 'components/core/ScrollRegion/ScrollRegion'
import TransitionSwap from 'components/elements/TransitionSwap/TransitionSwap'
import classNames from 'lib/util/classNames'
import styles from './SignUp.module.scss'
import PromptNoAccess from './prompts/PromptNoAccess/PromptNoAccess'
import PromptSignUp from './prompts/PromptSignUp/PromptSignUp'
import PromptSignUpPassportHolder from './prompts/PromptSignUpPassportHolder/PromptSignUpPassportHolder'

/**
 * The `SignUp` display
 *
 * @param {object} props - the component props
 * @returns {React.ReactElement} the element
 */
function SignUp (props) {
  const { className } = props
  const { scrollToTop } = useScrollRegionContext()
  const { account } = useUserContext()
  const [step, setStep] = useState('none')
  const classNameOutput = useMemo(
    () => classNames([className, styles.signUp]),
    [className]
  )

  /**
   * Memo: The step output. Depending on the status,
   * we display different outputs to the user
   */
  const output = useMemo(() => {
    if (step === 'passport_holder') {
      // User is a passport holder, and they did not use
      // an access code, passport holders do not need
      // an access code, and instead only need to input
      // their name & email, show them this form
      return <PromptSignUpPassportHolder />
    }

    if (step === 'standard_user') {
      if (!constants.IS_SIGN_UP_ENABLED) {
        // Sign ups aren't enabled
        return <PromptNoAccess />
      }

      return <PromptSignUp />
    }

    return null
  }, [step])

  /**
   * Layout Effect: On mount, apply the initial output
   */
  useLayoutEffect(() => {
    if (account?.isPassportHolder) {
      // User is a passport holder
      setStep('passport_holder')

      return
    }

    // User doesn't have an account + is not passport holder
    setStep('standard_user')
  }, [])

  /**
   * Effect: When the `step` changes, trigger
   * the modal to reset the scroll
   */
  useEffect(() => {
    scrollToTop()
  }, [step])

  return (
    <div className={classNameOutput}>
      <TransitionSwap animation="accordion" animateFirst={false}>
        {output}
      </TransitionSwap>
    </div>
  )
}

/** @type {object} */
SignUp.propTypes = {
  className: PropTypes.string
}

/** @type {object} */
SignUp.defaultProps = {
  className: ''
}

// Memoize
export default memo(SignUp)
